import React, { useEffect, useRef, useState } from 'react';
import { Field } from 'react-final-form';
import {
  Box, Button, Divider, HStack, Select, SimpleGrid, Text, useDisclosure,
} from '@chakra-ui/react';
import axios from 'axios';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { FiArrowRightCircle } from 'react-icons/fi';
import { ValidationErrors } from 'final-form';
import { NIL } from 'uuid';
import MarkdownTextInput from './MarkdownTextInput';
import DateInputControl from './DateInput';
import TextInputControl from './TextInput';
import TrackingUrlField from './TrackingUrlField'; // item 1310
import SelectInputControl from './SelectInput';
import ToggleInput from './ToggleInput';
import FormRow from './FormRow';
import CustomerProjectManagerField from './CustomerProjectManagerField';
import WorkOrderLeadsModal from '../WorkOrderLeadsModal'; // item 1173
import useUsers from '../../hooks/useUsers'; // item 1174b — lead names beside the button
import ProjectManagerField from './ProjectManagerField'; // item 833/C1
import { US_STATES } from '../../models/usStates';
import ResponsiveStack from '../ResponsiveStack';
import DocumentFormScaffold from './DocumentFormScaffold';
import SendDocumentButton from './SendDocumentButton'; // item 1247
import { useProjectContracts } from '../../hooks/useContracts'; // item 1264
import ProjectSelectField from './ProjectSelectField';
import DocumentPlatformField from './DocumentPlatformField';
import ServiceAddressSection from './ServiceAddressSection';
import ViewPdfButton from './ViewPdfButton';
import IconWorkOrder from '../../assets/Icon-WorkOrder.svg';
import IconWorkOrderWhite from '../../assets/Icon-WorkOrder-White.svg';
import useWorkOrder from '../../hooks/useWorkOrder';
import { WorkOrder, WorkOrderItem, WorkOrderStatus } from '../../models/workOrder';
import WorkOrderItemsFormElement from '../WorkOrderItemsFormElement';
import useContacts from '../../hooks/useContacts';
import { ContactDto } from '../../models/contactInfo';
import ReceivedPoAttachments from '../ReceivedPoAttachments';
import DocumentStatusStamp from '../DocumentStatusStamp';
import GenerateInvoiceModal from '../GenerateInvoiceModal';
import WorkOrderTrackedTime from '../WorkOrderTrackedTime'; // item 1343
import WorkOrderCreatePoModal from '../WorkOrderCreatePoModal'; // item 1315
import PmNotifyPromptModal from '../PmNotifyPromptModal'; // item 1364
import SalesOrderPortalPanel, { SalesOrderShareButton } from '../SalesOrderPortalPanel'; // item 1402
import { useSalesOrderPortal, salesOrderPortalKey } from '../../hooks/useSalesOrderPortal'; // item 1402

const validate = (values: WorkOrder): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (!values.workOrderTitle) {
    errors.workOrderTitle = 'Required';
  }
  // number is optional — left blank, the backend auto-generates it (item 101)
  if (!values.date) {
    errors.date = 'Required';
  }
  // every document must belong to a project (Chad, 2026-06-22)
  if (!values.projectId) {
    errors.projectId = 'Required';
  }
  // item 597 — Platform is REQUIRED for a STANDALONE work order (no source proposal); a
  // proposal-born WO inherits the proposal's platform, so it's pre-filled and optional here.
  if (!values.proposalId && !values.platform?.trim()) {
    errors.platform = 'Required';
  }
  return errors;
};

// item 1246/1247 — mirror the LIVE Document Type toggle OUT of the form (a second Field on the
// same name, render-only) so the chrome around it — page title, Save label, default first
// category, Send button type — tracks WO ↔ SO the moment the user flips it, not just on save.
const SoFlagEffect: React.FC<{ value: boolean; onValue: (v: boolean) => void }> = ({ value, onValue }) => {
  useEffect(() => { onValue(value); }, [value, onValue]);
  return null;
};
const SoFlagSync: React.FC<{ onValue: (v: boolean) => void }> = ({ onValue }) => (
  <Field name="isSalesOrder" subscription={{ value: true }}>
    {({ input }) => <SoFlagEffect value={String(input.value) === 'true'} onValue={onValue} />}
  </Field>
);

const WorkOrderForm: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const { tryUpdate, data: workOrder, status } = useWorkOrder({ id });
  // item 1364 — right after a standalone WO's FIRST save the scaffold lands here with a
  // promptPmEmail nav flag: ask once whether to email the PM (replaces the 1265 toggle).
  const pmLocation = useLocation<{ promptPmEmail?: boolean }>();
  const pmHistory = useHistory();
  const [pmPromptOpen, setPmPromptOpen] = useState(false);
  const pmPromptedRef = useRef(false);
  useEffect(() => {
    if (pmLocation.state?.promptPmEmail && id !== NIL && !pmPromptedRef.current) {
      pmPromptedRef.current = true;
      setPmPromptOpen(true);
      pmHistory.replace(pmLocation.pathname, {}); // one-shot: a refresh must not re-ask
    }
  }, [pmLocation, pmHistory, id]);
  // item 1246/1247 — the live WO-vs-SO flag (form value once mounted; saved value as the seed)
  const [isSo, setIsSo] = useState(false);
  // item 1402 — the customer-portal copy of a sales order; once one has EVER been minted the
  // Document Type is locked (a work order must never become portal-visible by a flip)
  const queryClient = useQueryClient();
  const { data: portalShare } = useSalesOrderPortal(id !== NIL ? id : undefined);
  const typeLocked = !!portalShare?.typeLocked;
  // item 1264 — is this WO's number written into contract paper on its project?
  const { data: woContracts } = useProjectContracts(workOrder?.projectId);
  const contractCount = ((): number => {
    const n = (workOrder?.workOrderNumber ?? '').trim().toUpperCase();
    if (!n) return 0;
    return (woContracts?.index ?? []).filter((c) => (c.number ?? '').trim().toUpperCase() === n).length;
  })();
  // item 812 (Chad) — convert this SO/WO straight to an invoice from the form footer
  const genModal = useDisclosure();
  const poModal = useDisclosure(); // item 1315 — SO/WO → vendor PO
  const leadsModal = useDisclosure(); // item 1173 — the leads + floor-verdict editor
  // item 1174b (Chad: "expose who the PL is, or nobody, off to the side") — the current
  // EFFECTIVE leads (WO rows, inheriting approval's picks) + the signed no-commission flag,
  // summarized beside the button; refreshed whenever the modal closes.
  const { data: usersData } = useUsers();
  const [leadSummary, setLeadSummary] = useState<{ userId: string; percent: number }[] | null>(null);
  const [leadSigned, setLeadSigned] = useState(false);
  useEffect(() => {
    if (id === NIL || leadsModal.isOpen) return;
    let cancelled = false;
    axios.get(`${(process.env.REACT_APP_BASIX_API || '')}WorkOrder/${id}/leads`)
      .then((r) => { if (!cancelled) setLeadSummary(r.data ?? []); })
      .catch(() => { if (!cancelled) setLeadSummary(null); });
    axios.get(`${(process.env.REACT_APP_BASIX_API || '')}WorkOrder/${id}/commission-basis`)
      .then((r) => { if (!cancelled) setLeadSigned(!!r.data?.noLeadCommission); })
      .catch(() => { /* summary only */ });
    // eslint-disable-next-line consistent-return
    return () => { cancelled = true; };
  }, [id, leadsModal.isOpen]);
  const leadNames = (leadSummary ?? [])
    .map((l) => {
      const name = (usersData?.index ?? []).find((u) => u.id === l.userId)?.name ?? '…';
      return leadSummary && leadSummary.length > 1 ? `${name} ${l.percent}%` : name;
    })
    .join(' · ');
  const { data: contacts, status: contactsStatus } = useContacts();

  // item 441 — the terminal-state ribbon: a WO closes when its invoices are generated
  // item 535 — a WO is locked server-side for ANY non-Generated status (WorkInProgress /
  // WorkComplete / InvoicesGenerated), plus void/archive. WorkOrderForm used to gate the
  // ribbon on InvoicesGenerated only AND never set hideSave, so a locked WO opened as an
  // editable form whose every save 400'd and wiped the text (Chad's duplicated-then-invoiced
  // WO). Compute the real locked state and both stamp it AND hide Save — mirrors Invoice/Proposal.
  const woLocked = id !== NIL && !!workOrder && (
    !!workOrder.isVoid
    || !!workOrder.isArchived
    || !!workOrder.isLocked
    || workOrder.status !== WorkOrderStatus.Generated
  );
  let statusStamp: React.ReactNode;
  if (workOrder && id !== NIL) {
    if (workOrder.isVoid) {
      statusStamp = <DocumentStatusStamp tone="negative" label="VOID" detail="This work order is void." />;
    } else if (workOrder.status === WorkOrderStatus.InvoicesGenerated) {
      statusStamp = <DocumentStatusStamp tone="neutral" label="CLOSED" detail="All invoices generated — the work order is locked. Voiding its last live invoice reopens it, or duplicate it for a fresh editable copy." />;
    } else if (workOrder.status === WorkOrderStatus.WorkInProgress && workOrder.hasLiveInvoice) {
      // item 1484 — partially invoiced by line (a backordered line still to bill)
      statusStamp = <DocumentStatusStamp tone="neutral" label="IN PROGRESS" detail="Partially invoiced — some line quantity is still to bill (backorder). Use ⋮ → Create another invoice on the ledger; it bills only what is left, and the order closes once every line is billed." />;
    } else if (workOrder.status === WorkOrderStatus.WorkInProgress || workOrder.status === WorkOrderStatus.WorkComplete) {
      statusStamp = <DocumentStatusStamp tone="neutral" label="LOCKED" detail="This work order is locked at its current status. Reopen it to Generated from the grid, or duplicate it for a fresh editable copy." />;
    }
  }

  const infoSection = (
    <>
      {/* item 1246/1247 — lift the live Document Type out to the scaffold chrome */}
      <SoFlagSync onValue={setIsSo} />
      {/* item 465 — Service Address (end-user) FIRST; the name IS the work-order title */}
      <ServiceAddressSection nameField="workOrderTitle" />
      <Divider my={4} />
      <FormRow
        heading="Document Type"
        subHeading="A Sales Order is a quick hardware/materials-only estimate (no labor). Both convert to an invoice the same way."
      >
        {/* item 38: Work Order vs Sales Order — a boolean stored on the work order */}
        {/* untyped Field: the value round-trips boolean (form state) <-> string (the
            <Select>), which 6.5.9's stricter one-type generic no longer models */}
        <Field
          name="isSalesOrder"
          parse={(v) => String(v) === 'true'}
          format={(v) => (v ? 'true' : 'false')}
        >
          {({ input }) => (
            <Select
              maxW="360px"
              value={input.value as unknown as string}
              onChange={input.onChange}
              aria-label="Document type"
              isDisabled={typeLocked}
              title={typeLocked ? 'Locked — this sales order has been shared to the customer portal' : undefined}
            >
              <option value="false">Work Order (labor + materials)</option>
              <option value="true">Sales Order (materials / hardware only)</option>
            </Select>
          )}
        </Field>
        {/* item 234/253/261 — the PDF price columns as an aligned, explained toggle grid
            (was bare checkboxes whose helper text collided with the Document Type column).
            Group price is the binding total; MSRP/SRP is a customer-facing suggestion. */}
        <Box mt={4}>
          <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" opacity={0.7} mb={1}>
            PDF price columns
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={8} spacingY={1}>
            <Field<boolean>
              name="showGroupPriceOnPdf"
              component={ToggleInput}
              defaultValue
              label="Show group price on PDF"
              helperText="The binding total the customer pays."
            />
            <Field<boolean>
              name="showMsrpOnPdf"
              component={ToggleInput}
              defaultValue
              label="Show SRP on PDF"
              helperText="A customer-facing suggested retail, for comparison."
            />
            {/* item 1336 (Chad) — T&M output option, default OFF: standard jobs unchanged */}
            <Field<boolean>
              name="showPerformedOnPdf"
              component={ToggleInput}
              label="Show who performed"
              helperText="Adds who performed each line beside its date (line dates always print). Carries to the invoice."
            />
            {/* item 1364 (Chad: "a pop-up after the initial save that asks if you want to email the
                project manager, vs just sending it and having to catch it in the form") — the 1265
                toggle is gone; the create no longer auto-emails, and PmNotifyPromptModal asks
                right after the first save (standalone WOs only — SOs never email, item 1232;
                proposal-born WOs are announced by the approval flow). */}
          </SimpleGrid>
        </Box>
      </FormRow>
      {/* item 1402 (Chad) — a SALES ORDER's customer-portal copy: share / re-share / withdraw */}
      {isSo && id !== NIL && (
        <FormRow
          heading="Customer portal"
          subHeading="Share this sales order so the customer can view the PDF and approve it in their portal. Emailing it shares it too. Work orders are never shared."
        >
          <SalesOrderPortalPanel workOrderId={id} disabled={!!workOrder?.isVoid || !!workOrder?.isArchived} />
        </FormRow>
      )}
      <Divider my={4} />
      <FormRow
        heading="Basic Information"
        subHeading="Information that will be the main identifiers for the work order"
      >
        <ResponsiveStack>
          <ProjectSelectField name="projectId" label="Project" />
          <Box w="100%">
            <TextInputControl
              name="workOrderNumber"
              label="Work Order Number"
              isRequired={false}
              placeholder="Uses your standardized numbering — fill in to override with a custom number"
            />
            {/* item 1264 (Chad: "this number is linked to contracts — really important") —
                when live contract paper is written against this number, say so right here,
                before anyone edits it. */}
            {contractCount > 0 && (
              <Text fontSize="xs" color="purple.300" mt={1} fontWeight="semibold">
                {`⚖ This is the number we contract with — written into ${contractCount} contract${contractCount === 1 ? '' : 's'} on this project. Changing it breaks the paper trail.`}
              </Text>
            )}
          </Box>
        </ResponsiveStack>
        <ResponsiveStack>
          <DateInputControl
            name="date"
            label="Date"
            isRequired
          />
          {/* item 597 — the work order's OWN platform: required for a standalone WO/SO; a
              proposal-born WO shows the inherited value (editable). Carries onto the invoice. */}
          <DocumentPlatformField
            isRequired={!workOrder?.proposalId}
            helperText={workOrder?.proposalId ? 'Inherited from the proposal — editable.' : 'Carries onto this work order’s invoice.'}
          />
        </ResponsiveStack>
        <ResponsiveStack>
          {/* item 527 — point of contact + phone are optional on a work order (Chad) */}
          <TextInputControl
            name="projectPointofContact"
            label="Point of Contact"
            isRequired={false}
          />
          <TextInputControl
            name="phoneNo"
            label="Phone Number"
            isRequired={false}
          />
          <SelectInputControl name="customerId" label="Customer" isRequired>
            {contactsStatus === 'success' && contacts && contacts.index.map((contactInfo: ContactDto) => <option key={contactInfo.id} value={contactInfo.id}>{contactInfo.name}</option>)}
          </SelectInputControl>
        </ResponsiveStack>
        {/* item 179 — the state whose sales-tax rate applies to the taxable lines */}
        <ResponsiveStack>
          <SelectInputControl
            name="taxState"
            label="Tax State"
            isRequired={false}
            helperText="Sets the sales-tax rate applied to taxable lines on this document."
          >
            <option value="">— No tax —</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>{`${s.code} — ${s.name}`}</option>
            ))}
          </SelectInputControl>
        </ResponsiveStack>
        {/* item 833/C1 — Project Manager (CRM-staff dropdown, from "Requested By") + item 833/C2
            Salesperson (the commission-wired picker, relabeled). Same pickers as the proposal. */}
        <ResponsiveStack>
          <ProjectManagerField />
          <CustomerProjectManagerField />
        </ResponsiveStack>
        {/* item 1173 (Chad: "it should collect that data inside of the work order") — the
            PROJECT LEADS editor, reachable from the WO itself (saved docs only — leads hang
            off the WO id). Opens the same modal as the grid/Bible, which now leads with the
            $1k floor verdict (lead pays vs recorded-only). Editable regardless of doc lock —
            leads are payout metadata, not contract content (item 526). */}
        {id !== NIL && (
          <Box mt={3}>
            <HStack spacing={3}>
              <Button size="sm" variant="outline" onClick={leadsModal.onOpen}>
                Project leads &amp; commission floor…
              </Button>
              {leadSummary !== null && (
                <Text fontSize="sm" color={leadNames ? undefined : 'orange.300'}>
                  {leadNames ? `Lead: ${leadNames}` : 'No project lead assigned'}
                  {leadSigned && (
                    <Text as="span" color="gray.500">{' · signed: no lead commission'}</Text>
                  )}
                </Text>
              )}
            </HStack>
            <WorkOrderLeadsModal
              workOrderId={id}
              workOrderNumber={workOrder?.workOrderNumber}
              isOpen={leadsModal.isOpen}
              onClose={leadsModal.onClose}
            />
          </Box>
        )}
      </FormRow>
      <Divider my={4} />
      {/* item 1488 (Chad) — the SCOPE area is its own section, kept apart from the service
          notes: on a proposal-born WO it holds the proposal's statement of work (copied at
          approval, editable); on a standalone service WO it is the client's request. Each
          prints on the invoice under its own output toggle. */}
      <FormRow
        heading={workOrder?.proposalId ? 'Proposal Scope' : 'Reason For Work'}
        subHeading={workOrder?.proposalId
          ? "The proposal's statement of work — off on the invoice PDF unless “Include proposal scope” is turned on"
          : 'Detailed description of the work going to be provided — prints on the service invoice'}
      >
        {/* item 532 — bind to the WO's real field (reasonForWorkOrder); this box was wired to
            statementOfWork (a proposal-only field), so it never loaded/saved and read blank on
            a duplicate even though the data copied. */}
        <Field<string>
          name="reasonForWorkOrder"
          component={MarkdownTextInput}
          label={workOrder?.proposalId ? 'Proposal Scope' : 'Work Description'}
        />
      </FormRow>
      <Divider my={4} />
      {/* item 543 — the technician logs WHAT THEY DID here; it's client-facing and carries
          onto the invoice PDF (alongside Reason for Work). Internal Notes below never prints. */}
      <FormRow
        heading="Work Performed (Service)"
        subHeading="The service notes — what was actually done on the job. Prints on the invoice by default; blank prints nothing"
      >
        <Field<string>
          name="workPerformed"
          component={MarkdownTextInput}
          label="Work Performed"
        />
      </FormRow>
      <Divider my={4} />
      {/* item 1343 — internal tracked-time costing panel (bid jobs); renders only with data */}
      {id !== NIL && <WorkOrderTrackedTime workOrderId={id} />}
      {/* item 1310 (Chad) — shipment tracking (from the PO conversion); carries to the invoice */}
      <FormRow heading="Shipment Tracking" subHeading="Optional — the carrier link; prints here and carries to the invoice">
        <TrackingUrlField docLabel="sales order" carriesTo="carries to the invoice" />
      </FormRow>
      <Divider my={4} />
      <FormRow
        heading="Internal Notes"
        subHeading="Internal only — challenges, gotchas, etc. Never shown to the client"
      >
        <Field<string>
          name="internalNotes"
          component={MarkdownTextInput}
          label="Notes"
        />
      </FormRow>
      {/* item 257 — the client's received PO authorizing this job */}
      {id !== NIL && <ReceivedPoAttachments docId={id} />}
      {/* item 1574 — the customer-signed hardware delivery document (rides to the invoice) */}
      {id !== NIL && <ReceivedPoAttachments docId={id} kind="delivery" />}
    </>
  );

  const scaffold = (
    <DocumentFormScaffold<WorkOrder, WorkOrderItem>
      config={{
        // item 1364 — after the FIRST save of a standalone WO (no proposal, not a sales order),
        // land on the edit route with the "email the PM?" prompt armed
        createNavState: (v: Record<string, unknown>) => (!v.proposalId && !v.isSalesOrder ? { promptPmEmail: true } : undefined),
        // item 1246/1247 — the chrome follows the live Document Type: a Sales Order says so
        // in the title and on the Save button, and its default first category is Equipment
        // (hardware-only doc) instead of Services.
        title: isSo ? 'SALES ORDER' : 'WORK ORDER',
        saveLabel: isSo ? 'Save Sales Order' : 'Save Work Order',
        defaultCategory: isSo ? 'Equipment' : 'Services',
        icon: IconWorkOrder,
        iconWhite: IconWorkOrderWhite,
        doc: workOrder,
        status,
        tryUpdate,
        validate,
        getItems: (doc) => doc.workOrderItems,
        getCategoryNames: (doc) => doc.itemCategories,
        applyScaffoldState: (values, state) => {
          values.itemCategories = state.categoryNames;
          values.workOrderItems = state.items;
          values.id = state.id;
          // item 234 — default the PDF price toggles on when untouched (new docs)
          if (values.showGroupPriceOnPdf === undefined) values.showGroupPriceOnPdf = true;
          if (values.showMsrpOnPdf === undefined) values.showMsrpOnPdf = true;
        },
        ItemsElement: WorkOrderItemsFormElement,
        infoSection,
        statusStamp,
        // item 535 — no editable-but-doomed form on a locked WO (also silences auto-save)
        hideSave: woLocked,
        footerActions: id !== NIL ? (
          <>
            {/* item 812 (Chad) — same convert-to-invoice as the WorkOrders ledger */}
            {!workOrder?.isVoid && !workOrder?.isArchived && (
              <Button size="sm" variant="ghost" leftIcon={<FiArrowRightCircle />} onClick={genModal.onOpen}>
                Convert to invoice
              </Button>
            )}
            {/* item 1315 (Chad) — price it as an order first, then cut the vendor PO from it */}
            {!workOrder?.isVoid && !workOrder?.isArchived && (
              <Button size="sm" variant="ghost" leftIcon={<FiArrowRightCircle />} onClick={poModal.onOpen}>
                Create purchase order
              </Button>
            )}
            {/* item 1247 (Chad) — email the WO/SO to the client, same dialog as invoices/POs;
                recipient auto-routes (assigned staff → customer main email, item 318) */}
            {!workOrder?.isVoid && !workOrder?.isArchived && (
              <SendDocumentButton
                documentType={isSo ? 'SalesOrder' : 'WorkOrder'}
                documentId={id}
                documentLabel={workOrder?.workOrderNumber}
                onSent={() => queryClient.invalidateQueries(salesOrderPortalKey(id))} // item 1402 — an emailed SO is now portal-shared
              />
            )}
            {/* item 1402 — sales orders only: share the frozen copy to the customer portal */}
            {isSo && !workOrder?.isVoid && !workOrder?.isArchived && <SalesOrderShareButton workOrderId={id} />}
            <ViewPdfButton path={`WorkOrder/${id}/pdf`} preview />
            <ViewPdfButton path={`WorkOrder/${id}/pdf`} />
          </>
        ) : undefined,
      }}
    />
  );

  return (
    <>
      {scaffold}
      {id !== NIL && (
        <>
          <WorkOrderCreatePoModal
            isOpen={poModal.isOpen}
            onClose={poModal.onClose}
            workOrderId={id}
            workOrderNumber={workOrder?.workOrderNumber}
            isSalesOrder={workOrder?.isSalesOrder}
            trackingUrl={workOrder?.trackingUrl}
          />
          <GenerateInvoiceModal
            isOpen={genModal.isOpen}
            onClose={genModal.onClose}
            workOrderId={id}
            isSalesOrder={workOrder?.isSalesOrder}
            fromProposal={!!workOrder?.proposalId}
          />
          {/* item 1364 — asked once, right after a standalone WO's first save */}
          <PmNotifyPromptModal workOrderId={id} isOpen={pmPromptOpen} onClose={() => setPmPromptOpen(false)} />
        </>
      )}
    </>
  );
};

export default withAuthenticationRequired(WorkOrderForm);
